import { isHeifFile, MAX_HEIC_BYTES } from './heicFiles';

interface HeicConversion {
  jpeg?: Blob;
}

const FAILED: HeicConversion = {};

const toJpegFileName = (name: string): string => `${name.replace(/\.hei[cf]$/i, '')}.jpg`;

async function convertInWorker(file: File): Promise<HeicConversion> {
  const worker = new Worker(new URL('heicToJpeg.worker.ts', import.meta.url), { type: 'module' });
  return new Promise<HeicConversion>(resolve => {
    worker.addEventListener('message', (event: MessageEvent<HeicConversion>) => resolve(event.data));
    worker.addEventListener('error', () => resolve(FAILED));
    file.arrayBuffer().then(
      buffer => worker.postMessage(buffer, [buffer]),
      () => resolve(FAILED)
    );
  }).finally(() => worker.terminate());
}

export interface ConvertedPhotos {
  photos: File[];
  unconverted: string[];
}

async function convertPhoto(file: File): Promise<File | undefined> {
  if (file.size > MAX_HEIC_BYTES) return undefined;
  const { jpeg } = await convertInWorker(file).catch(() => FAILED);
  return jpeg && new File([jpeg], toJpegFileName(file.name), { type: 'image/jpeg' });
}

export function convertHeicPhotos(
  files: File[],
  onConverting: (photoNumber: number) => void
): Promise<ConvertedPhotos> {
  let started = 0;
  return files.reduce<Promise<ConvertedPhotos>>(
    (previous, file) =>
      previous.then(async ({ photos, unconverted }) => {
        if (!isHeifFile(file)) return { photos: [...photos, file], unconverted };
        started += 1;
        onConverting(started);
        const converted = await convertPhoto(file);
        return converted
          ? { photos: [...photos, converted], unconverted }
          : { photos, unconverted: [...unconverted, file.name] };
      }),
    Promise.resolve({ photos: [], unconverted: [] })
  );
}
